import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import { FormikDatePicker } from "@/components/formcontrols/DatePicker";
import Modal, { ModalContext } from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  CourseCertificateEvidenceModel,
  UpdateCourseCertificateRequest,
} from "@/api/cle-api";
import { useUpdateCourseCertificate } from "@/hooks/cle-hooks";
import { dateToyyyyMMdd } from "@/util/date";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsNullable } from "../../../../../util/yup";
import {
  courseDurationToParts,
  formatCourseDuration,
  MAX_COURSE_DURATION_HOURS,
  MAX_COURSE_DURATION_MINUTES,
  partsToCourseDuration,
} from "./utils/courseDuration";

export interface CourseCertificateValidationFormValues {
  isLegalCourseCertificate: "" | "true" | "false";
  courseTitle: string;
  courseProvider: string;
  participantName: string;
  courseDate: Date | null;
  /** Kursets sidste dag. null er et endagskursus, ikke en ukendt slutdato. */
  courseEndDate: Date | null;
  /**
   * The duration as typed, split across two fields — "" means unset. Seeded as
   * strings, but Formik replaces them with numbers on change because the inputs
   * are type="number", so both shapes have to be tolerated.
   */
  courseTimeHours: string | number;
  courseTimeMinutes: string | number;
  numberOfLessons: string;
  signature: "" | "true" | "false";
  providerLogo: "" | "true" | "false";
  courseDescription: string;
}

// Minutes are deliberately not capped at 59: partsToCourseDuration carries the
// overflow into hours, so a certificate stating "90 minutter" can be typed as read.
const courseTimePart = (label: string) =>
  Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .typeError(`${label} skal være et tal`)
    .integer(`${label} skal angives i hele tal`)
    .min(0, `${label} kan ikke være negativ`);

const validationSchema = Yup.object({
  courseTitle: Yup.string().trim(),
  courseProvider: Yup.string().trim(),
  participantName: Yup.string().trim(),
  courseDate: Yup.date().nullable(),
  courseEndDate: Yup.date()
    .nullable()
    .min(Yup.ref("courseDate"), "Slutdatoen kan ikke ligge før kursusdatoen"),
  courseTimeHours: courseTimePart("Timer").test(
    "duration-within-limit",
    `Varighed kan højst være ${MAX_COURSE_DURATION_HOURS} timer (30 dage)`,
    (_value, context) => {
      const total = partsToCourseDuration(
        context.parent.courseTimeHours,
        context.parent.courseTimeMinutes,
      );
      return total === null || total <= MAX_COURSE_DURATION_MINUTES;
    },
  ),
  courseTimeMinutes: courseTimePart("Minutter"),
  numberOfLessons: YupNumberOfLessonsNullable(),
  courseDescription: Yup.string().trim(),
});

const parseBooleanField = (value: "" | "true" | "false"): boolean | null => {
  if (value === "") return null;
  return value === "true";
};

const booleanToField = (
  value: boolean | null | undefined,
): "" | "true" | "false" => {
  if (value == null) return "";
  return value ? "true" : "false";
};

export const certificateToValidationFormValues = (
  certificate: CourseCertificateEvidenceModel,
): CourseCertificateValidationFormValues => ({
  isLegalCourseCertificate:
    certificate.isLegalCourseCertificate === null
      ? ""
      : certificate.isLegalCourseCertificate
        ? "true"
        : "false",
  courseTitle: certificate.courseTitle ?? "",
  courseProvider: certificate.courseProvider ?? "",
  participantName: certificate.participantName ?? "",
  courseDate: certificate.courseDate ? new Date(certificate.courseDate) : null,
  courseEndDate: certificate.courseEndDate
    ? new Date(certificate.courseEndDate)
    : null,
  courseTimeHours: courseDurationToParts(certificate.courseTime).hours,
  courseTimeMinutes: courseDurationToParts(certificate.courseTime).minutes,
  numberOfLessons:
    certificate.numberOfLessons != null
      ? String(certificate.numberOfLessons)
      : "",
  signature:
    certificate.signature === null
      ? ""
      : certificate.signature
        ? "true"
        : "false",
  providerLogo: booleanToField(certificate.providerLogo),
  courseDescription: certificate.courseDescription ?? "",
});

export const validationFormValuesToUpdateRequest = (
  values: CourseCertificateValidationFormValues,
): UpdateCourseCertificateRequest => ({
  is_legal_course_certificate: parseBooleanField(
    values.isLegalCourseCertificate,
  ),
  course_title: values.courseTitle.trim() || null,
  course_provider: values.courseProvider.trim() || null,
  participant_name: values.participantName.trim() || null,
  course_date: values.courseDate ? dateToyyyyMMdd(values.courseDate) : null,
  course_end_date: values.courseEndDate
    ? dateToyyyyMMdd(values.courseEndDate)
    : null,
  course_time: partsToCourseDuration(
    values.courseTimeHours,
    values.courseTimeMinutes,
  ),
  number_of_lessons:
    values.numberOfLessons === "" ? null : Number(values.numberOfLessons),
  signature: parseBooleanField(values.signature),
  provider_logo: parseBooleanField(values.providerLogo),
  course_description: values.courseDescription.trim() || null,
});

interface CourseCertificateValidationEditFormProps {
  certificate: CourseCertificateEvidenceModel;
  onSave: (updated: CourseCertificateEvidenceModel) => void;
}

const CourseCertificateValidationEditForm = ({
  certificate,
  onSave,
}: CourseCertificateValidationEditFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateCertificate,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateCourseCertificate();

  const formik = useFormik<CourseCertificateValidationFormValues>({
    initialValues: certificateToValidationFormValues(certificate),
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      updateCertificate(
        {
          id: certificate.id,
          userId: certificate.ownerUserId,
          body: validationFormValuesToUpdateRequest(values),
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, (field, message) =>
              // The API validates the duration as one field. Land its error on
              // the hours input, which is where the group renders its message.
              formik.setFieldError(
                field === "courseTime" ? "courseTimeHours" : field,
                message,
              ),
            );
          },
        },
      );
    },
  });

  // The two fields read as one control: a single error line and one preview
  // below the pair, rather than a duplicated message under each input.
  const durationTouched = Boolean(
    formik.touched.courseTimeHours || formik.touched.courseTimeMinutes,
  );
  const durationError =
    formik.errors.courseTimeHours ?? formik.errors.courseTimeMinutes;
  const durationMinutes = partsToCourseDuration(
    formik.values.courseTimeHours,
    formik.values.courseTimeMinutes,
  );

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label className="form-label" htmlFor="isLegalCourseCertificate">
                Er det et juridisk kursusbevis?
              </label>
              <select
                className="form-select"
                id="isLegalCourseCertificate"
                name="isLegalCourseCertificate"
                value={formik.values.isLegalCourseCertificate}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              >
                <option value="">-</option>
                <option value="true">Ja</option>
                <option value="false">Nej</option>
              </select>
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="courseTitle">
                Kursustitel
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="courseTitle"
                name="courseTitle"
                type="text"
                value={formik.values.courseTitle}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="courseProvider">
                Kursusudbyder
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="courseProvider"
                name="courseProvider"
                type="text"
                value={formik.values.courseProvider}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="participantName">
                Deltagernavn
              </label>
              <input
                autoComplete="off"
                className="form-control"
                id="participantName"
                name="participantName"
                type="text"
                value={formik.values.participantName}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "courseDate",
                  label: "Kursusdato",
                  initialValue: "",
                  isRequired: false,
                }}
              />
            </div>
            {/* Slutdatoen afgør, om kurset løber ud over advokatens
                efteruddannelsesperiode. Tom = endagskursus. */}
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "courseEndDate",
                  label: "Kursets slutdato (kun flerdagskurser)",
                  initialValue: "",
                  isRequired: false,
                }}
              />
            </div>
            <fieldset className="flex-100">
              <legend className="form-label">Kursets varighed</legend>
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <label className="form-text mb-1" htmlFor="courseTimeHours">
                    Timer
                  </label>
                  <input
                    autoComplete="off"
                    className={`form-control${
                      durationTouched && formik.errors.courseTimeHours
                        ? " is-invalid"
                        : ""
                    }`}
                    id="courseTimeHours"
                    max={MAX_COURSE_DURATION_HOURS}
                    min="0"
                    name="courseTimeHours"
                    step="1"
                    type="number"
                    value={formik.values.courseTimeHours}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="flex-grow-1">
                  <label className="form-text mb-1" htmlFor="courseTimeMinutes">
                    Minutter
                  </label>
                  <input
                    autoComplete="off"
                    className={`form-control${
                      durationTouched && formik.errors.courseTimeMinutes
                        ? " is-invalid"
                        : ""
                    }`}
                    id="courseTimeMinutes"
                    min="0"
                    name="courseTimeMinutes"
                    step="1"
                    type="number"
                    value={formik.values.courseTimeMinutes}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
              <ValidationErrorMessage
                touched={durationTouched}
                errorMessage={durationError}
              />
              {durationMinutes !== null && !durationError && (
                <p className="form-text mb-0">
                  {formatCourseDuration(durationMinutes)}
                </p>
              )}
            </fieldset>
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                name="numberOfLessons"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="signature">
                Underskrevet
              </label>
              <select
                className="form-select"
                id="signature"
                name="signature"
                value={formik.values.signature}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              >
                <option value="">-</option>
                <option value="true">Ja</option>
                <option value="false">Nej</option>
              </select>
            </div>
            {/* Kriterie 3 er opfyldt af underskrift ELLER udbyderens logo, så en
                sagsbehandler, der ser et stempel AI'en overså, skal kunne rette
                det her. */}
            <div className="flex-100">
              <label className="form-label" htmlFor="providerLogo">
                Udbyders logo eller stempel
              </label>
              <select
                className="form-select"
                id="providerLogo"
                name="providerLogo"
                value={formik.values.providerLogo}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              >
                <option value="">-</option>
                <option value="true">Ja</option>
                <option value="false">Nej</option>
              </select>
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="courseDescription">
                Kursusbeskrivelse
              </label>
              <textarea
                className="form-control"
                id="courseDescription"
                name="courseDescription"
                rows={4}
                value={formik.values.courseDescription}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner ">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button color="primary" disabled={isPending} type="submit">
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface CourseCertificateValidationEditModalProps {
  certificate: CourseCertificateEvidenceModel;
  onSave: (updated: CourseCertificateEvidenceModel) => void;
}

export const CourseCertificateValidationEditModal = ({
  certificate,
  onSave,
}: CourseCertificateValidationEditModalProps) => (
  <Modal title="Redigér validering">
    <CourseCertificateValidationEditForm
      certificate={certificate}
      onSave={onSave}
    />
  </Modal>
);
